import React from "react";
import { Badge, Button, Input, Label, FormFeedback } from "reactstrap";
import { formatDate, parseLocalDate } from "../../../../common/service/CommonService";
import { Column } from "../../../../Components/Common/CommonTableIndexWise";
import { FormFieldConfig } from "Components/Common/CommonForm";
import { AMBULATORY_VISIT_ACTIVITY_CATEGORY_OPTIONS, AMBULATORY_VISIT_CATEGORY_OPTIONS, AMBULATORY_VISIT_TYPE_OPTIONS, BLOCK_REASONS_OPTIONS, DROPOUT_REASONS_OPTIONS, DROPOUT_TYPE_OPTIONS } from "api/modules/common.option";
import Commonbadge from "../../../../Components/Common/Commonbadge";
import TruncatedText from "../../../../Components/Common/TruncatedText";
import { validations } from "validations/FormValidations";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import CommonButton from "Components/Common/CommonButton";

export const visitcolumns = (handledetail: any): Column<any>[] => [
  {
    key: "visitnumber",
    label: "VISIT NO.",
    render: (val: any, row: any) => {
      const isVerified = row.visitstatus !== 'draft';
      if (!isVerified) {
        return (
          <span
            className="d-flex align-items-center gap-1 text-dark fw-semibold"
            title="Verify visit to view details"
          >
            {val || "--"}
          </span>
        );
      }
      return (
        <a
          href="#"
          style={{ textDecoration: "none" }}
          onClick={(e) => {
            e.preventDefault();
            handledetail(row);
          }}
        >
          <span className="d-flex align-items-center gap-1 text-primary fw-semibold cursor-pointer hover-underline">
            {val || "--"}
          </span>
        </a>
      );
    }
  },
  {
    key: "visitname",
    label: "VISIT NAME",
    render: (val: any) => {
      return (
        <div>
          <TruncatedText text={val || "--"} maxWidth="200px" />
        </div>
      );
    }
  },

  {
    key: "samplingpoints",
    label: "SAMPLING POINTS",
    render: (val: any) => {
      let displayValue = "--";
      
      if (Array.isArray(val) && val.length > 0) {
        displayValue = val.filter(Boolean).join(", ");
      } else if (val) {
        displayValue = String(val);
      }

      return (
        <div className="d-flex flex-column">
          <span className="fw-semibold text-dark">
            <TruncatedText text={displayValue} maxWidth="150px" />
          </span>
        </div>
      );
    }
  },
  {
    key: "assignedsubjectcount",
    label: "SUBJECTS",
    render: (val: any) => {
      return (
        <div className="d-flex flex-column">
          <span className="fw-semibold text-dark">{val || 0} Assigned</span>
        </div>
      );
    }
  },
  {
    key: "visitstatus",
    label: "VISIT STATUS",
    render: (val: any) => {
      const status = val || "scheduled";
      return <Commonbadge status={status} style={{ width: "90px", fontSize: "12px" }} />;
    }
  },
];

export const section1Fields: FormFieldConfig[] = [
  {
    name: "visitname",
    label: "Visit Name",
    type: "text",
    required: true,
    placeholder: "e.g., Follow-up Visit 1",
    rules: validations.ambulatoryVisit.visitname
  },
  {
    name: "visittype",
    label: "Visit Type",
    type: "select",
    required: true,
    options: AMBULATORY_VISIT_TYPE_OPTIONS,
    placeholder: "Select visit type...",
  },
  {
    name: "visitcategory",
    label: "Visit Category",
    type: "select",
    required: true,
    options: AMBULATORY_VISIT_CATEGORY_OPTIONS,
    placeholder: "Select visit category...",
  },
];

export const section1Desc: FormFieldConfig[] = [
  {
    name: "visitdescription",
    label: "Visit Description",
    type: "textarea",
    placeholder: "Provide additional details or instructions for this visit (optional).",
    rules: validations.ambulatoryVisit.visitdescription
  }
];

export const getSubjectAssessmentFields = (disabled: boolean = false): FormFieldConfig[] => [
  {
    name: "subjectreported",
    label: "Participant Reported",
    type: "radio",
    required: true,
    rules: { required: "Participant Reported is required" },
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
    disabled,
  },
  {
    name: "idcardgiven",
    label: "ID Card Given",
    type: "radio",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
    disabled,
  },
  {
    name: "breathalcoholresult",
    label: "Alcohol Test Result",
    type: "radio",
    required: true,
    rules: { required: "Alcohol Test Result is required" },
    options: [
      { label: "Negative", value: "negative" },
      { label: "Positive", value: "positive" },
    ],
    disabled,
  },
  {
    name: "breathalcoholcolor",
    label: "Color of indicator light",
    type: "text",
    placeholder: "e.g., Green",
    disabled,
  },
  {
    name: "breathalcoholconcentration",
    label: "Alcohol concentration (mg/100ml)",
    type: "text",
    placeholder: "e.g., 0.00",
    rules: validations.ambulatoryVisit.breathalcoholconcentration,
    disabled,
  },
  {
    name: "identificationtime",
    label: "Identification Time (By ID card)",
    type: "time",
    required: true,
    rules: { required: "Identification Time is required" },
    disabled,
  },
];

export const section2Fields = (getValues: any): FormFieldConfig[] => {
  return [
    {
      name: "scheduleddate",
      label: "Scheduled Visit Date",
      type: "date",
      required: true,
      optionsProps: {
        minDate: "today"
      },
      rules: {
        validate: (val: any) => {
          if (!val) return true;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const selected = new Date(val);
          selected.setHours(0, 0, 0, 0);
          if (selected < today) {
            return "Scheduled Visit Date cannot be in the past.";
          }
          return true;
        }
      }
    },

    {
      name: "visitstartday",
      label: "Visit Window Start (Days)",
      type: "text",
      required: true,
      placeholder: "e.g., -2",
      rules: validations.ambulatoryVisit.visitstartday
    },
    {
      name: "visitendday",
      label: "Visit Window End (Days)",
      type: "text",
      required: true,
      placeholder: "e.g., +2",
      rules: validations.ambulatoryVisit.visitendday(getValues)
    }
  ];
};

// Table Columns Definition
export const activitycolumns: any[] = [
  {
    key: "activityname",
    label: "Activity Name",
    render: (val: any) => (
      <div className="d-flex align-items-center gap-2">
        <div className="avatar-xs rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center" style={{ width: "24px", height: "24px" }}>
          <i className="ri-file-text-line fs-13" />
        </div>
        <TruncatedText text={val || "--"} maxWidth="200px" />
      </div>
    )
  },
  {
    key: "activitydescription",
    label: "Description",
    render: (val: any) => (
      <TruncatedText text={val || "--"} maxWidth="250px" />
    )
  },
  {
    key: "activitycategory",
    label: "Activity Category",
    render: (val: any, row: any) => {
      const opt = AMBULATORY_VISIT_ACTIVITY_CATEGORY_OPTIONS.find(o => String(o.value) === String(val));
      const label = opt ? opt.label : row.activitycategoryname || val || "--";
      return (
        <span className="badge bg-light text-primary border border-primary-subtle px-2 py-1 rounded-pill fs-14 fw-semibold">
          {label}
        </span>
      );
    }
  },
  {
    key: "estimateddurationminutes",
    label: "Estimated Duration",
    render: (val: string, row?: any) => {
      return (
        <span className="d-inline-flex align-items-center gap-1 text-success bg-success-subtle border border-success-subtle px-2 rounded-pill fs-14 fw-semibold">
          <i className="ri-time-line text-success fs-12" />
          {val ? `${val} mins` : "--"}
        </span>
      );
    }
  },
  {
    key: "ismandatory",
    label: "Mandatory",
    render: (val: boolean) => {
      if (val) {
        return (
          <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1 rounded-pill fs-14 fw-semibold d-inline-flex align-items-center gap-1">
            Mandatory
          </span>
        );
      }
      return (
        <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle px-2 py-1 rounded-pill fs-14 fw-semibold">
          Optional
        </span>
      );
    }
  }
];

export const activityFormFields: FormFieldConfig[] = [
  {
    name: "activityname",
    label: "Activity Name",
    type: "text",
    required: true,
    placeholder: "e.g., Vital Signs",
    rules: validations.ambulatoryVisit.activityname
  },
  {
    name: "activitydescription",
    label: "Activity Description",
    type: "text",
    placeholder: "e.g., Record vital signs and measurements",
    rules: validations.ambulatoryVisit.activitydescription
  },
  {
    name: "activitycategory",
    label: "Activity Category",
    type: "select",
    required: true,
    options: AMBULATORY_VISIT_ACTIVITY_CATEGORY_OPTIONS,
    placeholder: "Select category...",
  },
  {
    name: "estimateddurationminutes",
    label: "Estimated Duration (Min)",
    type: "number",
    required: true,
    placeholder: "Enter duration",
    rules: validations.ambulatoryVisit.estimateddurationminutes
  },
  {
    name: "ismandatory",
    label: "Mandatory for this visit",
    type: "checkbox",
  }
];

export const subjectColumns = (
  navigate: any,
  onCheckInClick?: (row: any) => void,
  visitStatus?: string,
  hasPermission?: (module: string, action: string) => boolean
): Column<any>[] => [
    {
      key: "subjectno",
      label: "SUBJECT ID",
      render: (_: any, row: any) => {
        const val = row.subjectno || 0;
        const status = (row.subjectvisitstatus || row.status || "").toLowerCase();
        const isView = ["completed", "checked out", "activities completed"].includes(status);
        const isClickable = ["checked out", "activities completed"].includes(status) &&
          (hasPermission ? hasPermission(PERMISSION_MODULES.AMBULATORY_VISIT, PERMISSION_ACTIONS.VIEW) : true);
        return (
          <span
            className={isClickable ? "fw-bold text-primary cursor-pointer hover-underline" : "fw-bold text-dark"}
            style={{ cursor: isClickable ? "pointer" : "default" }}
            {
            ...(
              isClickable
                ? {
                  onClick: () =>
                    navigate(
                      isView
                        ? `./subject/${val}/view`
                        : `./subject/${val}`
                    )
                }
                : {}
            )
            }
          >
            {val}
          </span>
        );
      }
    },
    {
      key: "checkin",
      label: "CHECK-IN DATE & TIME",
      render: (_: any, row: any) => {
        const date = row.checkindate || row.checkinDate;
        const time = row.checkintime || row.checkinTime;
        if (!date && !time) return <span className="text-muted">--</span>;
        return (
          <div className="d-flex flex-column gap-1">
            {date ? (
              <span className="text-dark">
                <i className="ri-calendar-line text-primary me-1" />
                {formatDate(date)}
              </span>
            ) : null}
            {time ? (
              <small className="text-muted">
                <i className="ri-time-line text-primary me-1" />
                {time}
              </small>
            ) : null}
          </div>
        );
      }
    },
    {
      key: "checkinremarks",
      label: "CHECK IN REMARKS",
      render: (_: any, row: any) => {
        const remarks = row.checkinremarks || row.checkinRemarks;
        return (
          <div className="d-flex flex-column gap-1 fw-semibold fs-14">
            <TruncatedText text={remarks || "--"} maxWidth="200px" />
          </div>
        );
      }
    },
    {
      key: "checkout",
      label: "CHECK-OUT DATE & TIME",
      render: (_: any, row: any) => {
        const date = row.checkoutdate || row.checkoutDate;
        const time = row.checkouttime || row.checkoutTime;
        if (!date && !time) return <span className="text-muted">--</span>;
        return (
          <div className="d-flex flex-column gap-1">
            {date ? (
              <span className="text-dark">
                <i className="ri-calendar-line text-primary me-1" />
                {formatDate(date)}
              </span>
            ) : null}
            {time ? (
              <small className="text-muted">
                <i className="ri-time-line text-primary me-1" />
                {time}
              </small>
            ) : null}
          </div>
        );
      }
    },
    {
      key: "checkoutremarks",
      label: "CHECK OUT REMARKS",
      render: (_: any, row: any) => {
        const remarks = row.checkoutremarks || row.checkoutRemarks;
        return (
          <div className="d-flex flex-column gap-1 fw-semibold fs-14">
            <TruncatedText text={remarks || "--"} maxWidth="200px" />
          </div>
        );
      }
    },
    {
      key: "status",
      label: "SUBJECT VISIT STATUS",
      render: (_: any, row: any) => {
        return <Commonbadge status={row.subjectvisitstatus || "assigned"} style={{ width: "130px", fontSize: "12px" }} />;
      }
    },
    {
      key: "decisionstatus",
      label: "DECISION STATUS",
      render: (_: any, row: any) => {
        return <Commonbadge status={row.decisionstatus || "active"} style={{ width: "130px", fontSize: "12px" }} />;
      }
    },
    ...((hasPermission && (
      hasPermission(PERMISSION_MODULES.AMBULATORY_VISIT, PERMISSION_ACTIONS.CHECK_IN) ||
      hasPermission(PERMISSION_MODULES.AMBULATORY_VISIT, PERMISSION_ACTIONS.CHECK_OUT) ||
      hasPermission(PERMISSION_MODULES.AMBULATORY_VISIT, PERMISSION_ACTIONS.PERFORM_ACTIVITIES)
    )) ? [
      {
        key: "actions",
        label: "ACTIONS",
        render: (_: any, row: any) => {
          const status = (row.subjectvisitstatus || "").toLowerCase();

          return (
            <div className="d-flex align-items-center gap-2">
              {status === "completed" || status === "checked out" ? (
                <Commonbadge status={"Completed"} style={{ width: "90px", fontSize: "12px" }} />
              ) : status === "activities completed" ? (
                <CommonButton
                  module={PERMISSION_MODULES.AMBULATORY_VISIT}
                  action={PERMISSION_ACTIONS.CHECK_OUT}
                  size="md"
                  color="dark"
                  outline
                  style={{ borderRadius: 30 }}
                  onClick={() => onCheckInClick && onCheckInClick(row)}
                >
                  <i className="ri-logout-box-line" /> Check-Out
                </CommonButton>
              ) : status === "checked in" ? (
                <CommonButton
                  module={PERMISSION_MODULES.AMBULATORY_VISIT}
                  action={PERMISSION_ACTIONS.PERFORM_ACTIVITIES}
                  size="md"
                  color="primary"
                  style={{ borderRadius: 30 }}
                  onClick={() => navigate(`./subject/${row.subjectno || row.subjectId}`)}
                >
                  <i className="ri-pencil-line" /> Perform Activity
                </CommonButton>
              ) : (
                <CommonButton
                  module={PERMISSION_MODULES.AMBULATORY_VISIT}
                  action={PERMISSION_ACTIONS.CHECK_IN}
                  size="md"
                  color="primary"
                  outline
                  style={{ borderRadius: 30 }}
                  onClick={() => onCheckInClick && onCheckInClick(row)}
                  disabled={visitStatus !== "started" && visitStatus !== "In Progress"}
                >
                  <i className="ri-login-box-line" /> Check-In
                </CommonButton>
              )}
            </div>
          );
        }
      }
    ] : [])
  ];

export const assignSubjectColumns = (handleSelectToggle: any, selectedSubjectNos: any) => [
  {
    key: "select",
    label: "SELECT",
    textCenter: true,
    width: "80px",
    render: (_: any, row: any) => (
      <div className="d-flex align-items-center justify-content-center">
        <Input
          type="checkbox"
          checked={selectedSubjectNos.includes(String(row.subjectno))}
          onChange={() => handleSelectToggle(row)}
          style={{ cursor: "pointer" }}
        />
      </div>
    )
  },
  {
    key: "subjectno",
    label: "SUBJECT",
    textCenter: true,
    width: "80px",
    render: (_: any, row: any) => (
      <span className="fw-semibold text-dark">
        {String(row.subjectno || "").padStart(2, "0")}
      </span>
    )
  }
];

export const checkInFormFields: FormFieldConfig[] = [
  {
    name: "checkinDate",
    label: "Check-In Date",
    type: "date",
    disabled: true,
    required: true,
  },
  {
    name: "checkinTime",
    label: "Check-In Time",
    type: "time",
    required: true,
  },
  {
    name: "remarks",
    label: "Remarks",
    type: "textarea",
    placeholder: "Enter check-in/out remarks...",
  }
];

export const checkInFields = (minD: any, maxD: any): FormFieldConfig[] => [
  {
    name: "checkinDate",
    label: "Check-In Date",
    type: "flatpickr",
    required: true,
    optionsProps: {
      minDate: minD,
      maxDate: maxD,
    }
  },
  {
    name: "checkinTime",
    label: "Check-In Time",
    type: "time",
    required: true,
  }
];

export const checkOutFields = (convert12to24: any, display12h: any, checkinTime: any): FormFieldConfig[] => [
  {
    name: "checkoutDate",
    label: "Check-Out Date",
    type: "flatpickr",
    disabled: true,
    required: true,
  },
  {
    name: "checkoutTime",
    label: "Check-Out Time",
    type: "time",
    required: true,
    rules: {
      validate: (val: any) => {
        if (!val) return true;
        const inTime24 = checkinTime ? convert12to24(checkinTime) : "";
        const val24 = val ? convert12to24(val) : "";
        if (inTime24 && val24 < inTime24) {
          const displayInTime = display12h(checkinTime);
          return `Check-out time cannot be before check-in time (${displayInTime}).`;
        }
        return true;
      }
    }
  }
];

export const remarksFields: FormFieldConfig[] = [
  {
    name: "remarks",
    label: "Remarks",
    type: "textarea",
    placeholder: "Enter check-in/out remarks...",
    rules: validations.ambulatoryVisit.remarks
  }
];

export const overallStatusFields: FormFieldConfig[] = [
  {
    name: "status",
    label: "Select Status",
    type: "radio",
    required: true,
    options: [
      { label: "Completed", value: "completed" },
      { label: "Dropped", value: "dropped" },
      { label: "Blocked", value: "blocked" },
    ],
  },
];

export const getDropoutFields = (dropDate: string | null, dropType: string | null, STUDY_STAGES: any): FormFieldConfig[] => {
  const selectedType = DROPOUT_TYPE_OPTIONS.find(
    (opt) => String(opt.value) === String(dropType) || opt.label === dropType
  )?.label || (dropType ? String(dropType) : null);

  const isAE = selectedType === "Medical / AE";

  const fields: FormFieldConfig[] = [
    {
      name: "dropouttype",
      label: "Dropout Type",
      type: "select",
      required: true,
      options: DROPOUT_TYPE_OPTIONS,
      placeholder: "Select dropout type...",
    },
    {
      name: "dropoutdate",
      label: "Dropout Date",
      type: "date",
      required: true,
    },
    {
      name: "studystage",
      label: "Study Stage",
      type: "select",
      required: true,
      options: STUDY_STAGES,
      placeholder: "Select Stage...",
    },
    {
      name: "effectivedate",
      label: "Effective Date",
      type: "date",
      required: true,
      disabled: !dropDate,
      optionsProps: {
        minDate: dropDate ? parseLocalDate(dropDate) || undefined : undefined,
      },
    },
    {
      name: "dropoutreason",
      label: "Dropout Reason",
      type: "select",
      required: true,
      options: DROPOUT_REASONS_OPTIONS,
      placeholder: "Select Reason...",
    },
    {
      name: "detailedreason",
      label: "Detailed Reason",
      type: "textarea",
      placeholder: "Enter detailed reason here...",
      rules: validations.ambulatoryVisit.detailedreason
    },
  ];

  if (isAE) {
    fields.splice(1, 0, {
      name: "aereferenceno",
      label: "AE Reference No.",
      type: "text",
      required: true,
      placeholder: "e.g. AE-2024-017",
      disabled: true
    });
  }

  return fields;
};

export const getBlockTypeFields = (blockTypeValue: any): FormFieldConfig[] => [
  {
    name: "blocktype",
    label: "Block Type",
    type: "radio",
    required: true,
    options: [
      { label: "Temporary", value: "Temporary" },
      { label: "Permanent", value: "Permanent" },
    ],
    description:
      blockTypeValue === "Temporary"
        ? "Temporary blocks require a defined end date."
        : "Permanent blocks are lifetime exclusions and cannot be reversed automatically.",
  },
];

export const getBlockDateFields = (blockType: string | null, startDate: string | null): FormFieldConfig[] => {
  const fields: FormFieldConfig[] = [
    {
      name: "blockstartdate",
      label: "Block Start Date",
      type: "date",
      required: true,
    },
  ];

  if (blockType === "Temporary") {
    fields.push({
      name: "blockenddate",
      label: "Block End Date",
      type: "date",
      required: true,
      disabled: !startDate,
      optionsProps: {
        minDate: startDate ? parseLocalDate(startDate) || undefined : undefined,
      },
    });
    fields.push({
      name: "blockduration",
      label: "Block Duration",
      type: "text",
      disabled: true,
    });
  }

  return fields;
};

export const getBlockReasonFields = (): FormFieldConfig[] => [
  {
    name: "blockreason",
    label: "Block Reason",
    type: "select",
    required: true,
    options: BLOCK_REASONS_OPTIONS,
    placeholder: "Select Reason...",
  },
  {
    name: "detailedremarks",
    label: "Detailed Remarks",
    type: "textarea",
    placeholder: "Enter detailed remarks here...",
    rules: validations.ambulatoryVisit.detailedremarks
  },
];

export const eligibilityFields: FormFieldConfig[] = [
  {
    name: "rescreeningeligibility",
    label: "Re-screening Eligibility",
    type: "radio",
    options: [
      { label: "Eligible after block period", value: "Eligible after block period" },
      { label: "Requires PI review", value: "Requires PI review" },
    ],
  },
];

export const activityColumns = (handleActivityResultChange: any, handleActivityFieldChange: any) => [
  {
    key: "name",
    label: "Activity Name",
    render: (val: any, row: any) => (
      console.log(row),
      <>
        <span className="fw-bold text-dark me-2 fs-13 d-inline-flex align-items-center">
          <TruncatedText text={val || "--"} maxWidth="200px" />
          {row.ismandatory === true && <span className="text-danger ms-1">*</span>}
        </span>
      </>
    ),
    width: "300px"
  },
  {
    key: "result",
    label: "Result",
    render: (_: any, row: any) => (
      <div className="d-flex align-items-center gap-3 fs-13">
        <Label className="d-flex align-items-center gap-1 mb-0 cursor-pointer small fw-semibold text-success fs-13">
          <Input
            type="checkbox"
            checked={row.result === "Passed"}
            onChange={() => handleActivityResultChange(row.id, "Passed")}
            style={{ borderRadius: "4px", height: "17px" }}
          />
          Passed
        </Label>
        <Label className="d-flex align-items-center gap-1 mb-0 cursor-pointer small fw-semibold text-danger fs-13">
          <Input
            type="checkbox"
            checked={row.result === "Failed"}
            onChange={() => handleActivityResultChange(row.id, "Failed")}
            style={{ borderRadius: "4px", height: "17px" }}
          />
          Failed
        </Label>
      </div>
    ),
    width: "300px"
  },
  {
    key: "remarks",
    label: "Remarks",
    render: (val: any, row: any) => {
      const validationResult = validations.ambulatoryVisit.remarks.validate(val || "");
      const isInvalid = typeof validationResult === "string";
      return (
        <div className="position-relative">
          <Input
            type="text"
            placeholder="Enter remarks (optional)"
            bsSize="sm"
            value={val || ""}
            invalid={isInvalid}
            onChange={(e) => handleActivityFieldChange(row.id, "remarks", e.target.value)}
            style={{ borderRadius: "6px", fontSize: "12px", width: "100%" }}
            title={val || "Enter remarks (optional)"}
          />
          {isInvalid && (
            <FormFeedback className="d-block position-absolute" style={{ fontSize: "10px", marginTop: "2px", zIndex: 5 }}>
              {validationResult}
            </FormFeedback>
          )}
        </div>
      );
    },
    width: "400px"
  }
];

